import React, { FC, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Menu, MenuItem } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { StoreActionType } from '../../../stores/redux/types';
import { fetchLeftMenu } from '../../../stores/redux/actions/getLeftMenuActions/_getLeftMenuActions';
import Stack from '@mui/material/Stack';
import {
    SCUIMainMenu,
    SCUIDarkModeSwitch,
    SCUIGeneralLanguageSelect,
    SCUIHeaderSearch,
    SCUIStoreSelect,
} from '../../atoms';

import Helper from '../../../utils/helper';
import AuthService from '../../../services/auth/Auth.services';
import Typography from '@mui/material/Typography';
import jwt_decode from 'jwt-decode';
import { PageUrl } from '../../../types/enums';
import { useTranslation } from 'react-i18next';
import MenuIcon from '@mui/icons-material/Menu';
import ClearIcon from '@mui/icons-material/Clear';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import RefreshIcon from '@mui/icons-material/Refresh';
import './SCUIHeader.scss';
import { ISCUIHeader, Drawer } from './SCUIHeader.config';
import { debounceResize, filterHeaderMenuByQuery } from './scuiHeader.helpers';
import useMediaQuery from '@mui/material/useMediaQuery';
import StyleVariables from '../../../assets/styles/variables/_export.module.scss';
import { useHistory } from 'react-router-dom';
import LocalStorageWrapper from '../../../core/wrappers/browserStorage/localStorage-wrapper';
import Avatar from '@mui/material/Avatar';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { blue, lightBlue } from '@mui/material/colors';
import Person from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import SecurityIcon from '@mui/icons-material/Security';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import logo from '../../../images/logo.png';
import newYearLogo from '../../../assets/images/png/backoffice_new_year_2026.png';
import { SCUIGeneralCountrySelectV2 } from '../../atoms/SCUIGeneralCountrySelect/SCUIGeneralCountrySelectV2';
import { SCUIHoverEnvironment } from '../../molecules/SCUIHoverEnvironment/SCUIHoverEnvironment';
import { AuthQuery, GeneralQuery, MenuQuery } from '../../../services/query';
import Tooltip from '@mui/material/Tooltip';
let instanceId = parseInt(import.meta.env.VITE_INSTANCEID || '1');

// New Year logo visibility check
const shouldShowNewYearLogo = (): boolean => {
    const now = new Date();
    const newYearDeadline = new Date('2026-01-10T00:00:00');
    const envName = (import.meta.env.VITE_ENVIRONMENT || '').toString().toLowerCase();
    const isDevEnvironment = ['local', 'dev', 'test', 'preprod'].includes(envName);
    const isTargetInstance = instanceId === 5 || instanceId === 9;

    // Show new year logo if:
    // 1. Date is before January 10, 2026
    // 2. AND (instanceId is 5 or 9 OR it's a dev environment)
    return now < newYearDeadline && (isTargetInstance || isDevEnvironment);
};

export const SCUIHeader: FC<ISCUIHeader> = () => {
    const [isLogin, setIsLogin] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredMenuContent, setFilteredMenuContent] = useState<any>(null);
    const menuContentStore: any = useSelector((state: any) => state);
    const isMobile = useMediaQuery(`(max-width:  ${StyleVariables.breakpointSm})`, { noSsr: true });
    const isTablet = useMediaQuery(`(max-width:  ${StyleVariables.breakpointMd})`, { noSsr: true });
    const dispatch: any = useDispatch();
    const menuStore: any = useSelector((state: any) => state?.menu);
    const history = useHistory();
    const authService = new AuthService();
    const [countryDatas, setCountryDatas] = useState<any>([]);
    const { t } = useTranslation();

    const [paperClassName, setPaperClassName] = React.useState(menuStore?.isMenuOpen ? 'menuOpened' : 'menuClosed');

    const [darkState, setDarkState] = useState(localStorage.getItem('isDarkMode') === 'true');

    const [userName, setUserName] = React.useState('');
    const [userInitials, setUserInitials] = useState('');
    const [envVisible, setEnvVisible] = React.useState(false);
    const [showLogo, setShowLogo] = React.useState(menuStore?.isMenuOpen);
    const [isRefreshingMenu, setIsRefreshingMenu] = useState(false);

    // Helper functions to reduce ternary complexity
    const getThemeColors = () => ({
        palletType: darkState ? ('dark' as const) : ('light' as const),
        mainPrimaryColor: darkState ? blue[500] : lightBlue[500],
        mainColor: darkState ? '#1e1e1e' : '#fff',
    });

    // Helper function to get drawer width based on menu state
    const getDrawerWidth = () => {
        if (!menuStore?.isMenuOpen) return '64px';
        return isMobile ? '100%' : '300px';
    };

    // Helper function to get drawer paper width
    const getDrawerPaperWidth = () => {
        if (!menuStore?.isMenuOpen) return '64px';
        return isMobile ? '100%' : '250px';
    };

    const { palletType, mainPrimaryColor, mainColor } = getThemeColors();
    const darkTheme = createTheme({
        palette: {
            mode: palletType,
            primary: {
                main: mainPrimaryColor,
            },
            secondary: {
                main: mainColor,
            },
        },
    });
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleProfileClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleProfileClose();
        handleClickIcon();
    };

    const handleRoleRequest = () => {
        history.push(PageUrl.requestRole.url);
        setAnchorEl(null); // Close the menu
    };

    const handleMyRoleRequests = () => {
        history.push(PageUrl.myRoleRequests.url);
        setAnchorEl(null); // Close the menu
    };

    const handleRefreshMenu = async () => {
        setIsRefreshingMenu(true);
        try {
            // Cache'i temizlemek için API çağrısı yap
            await dispatch(fetchLeftMenu(true));
            // Ardından tüm sayfayı yenile (loading ile birlikte)
            window.location.reload();
        } catch (error) {
            console.error('Menu refresh error:', error);
            setIsRefreshingMenu(false);
        }
    };

    const isHeaderAccordionOpen = true;

    useEffect(() => {
        const envName: any = import.meta.env.VITE_ENVIRONMENT.toString().toLowerCase();
        const countryId: any = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId?.toString();

        const storedCountryData = localStorage.getItem('countryData') || '';

        if (storedCountryData && storedCountryData !== 'undefined') {
            setCountryDatas(JSON.parse(storedCountryData));
        } else {
            getAllActiveCountryLanguagesForBackoffice();
        }

        // Helper function to check environment visibility
        const shouldShowEnvironment = () => {
            const devEnvironments = ['local', 'dev', 'test', 'preprod'];
            return countryId == '1' && devEnvironments.includes(envName);
        };

        if (shouldShowEnvironment()) {
            setEnvVisible(true);
        }
    }, []);
    useEffect(() => {
        if (menuContentStore?.menuContent?.menuItem) setFilteredMenuContent(null);
    }, [menuContentStore]);
    useEffect(() => {
        loginCheck();
        dispatch({
            type: StoreActionType.SET_LEFTMENU_OPEN,
            payload: {
                isLeftMenuOpen: !isTablet,
            },
        });
    }, []);

    useEffect(() => {
        const handleResize = debounceResize(() => {
            if (window.innerWidth < 764 && menuStore?.isMenuOpen) {
                _menuClose();
            }
        }, 150);

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [menuStore?.isMenuOpen]);

    const clickNestedMenu = () => {
        if (isMobile) {
            _menuClose();
        }
    };

    const _menuClose = () => {
        dispatch({
            type: StoreActionType.SET_LEFTMENU_OPEN,
            payload: {
                isLeftMenuOpen: false,
            },
        });
    };

    const handleThemeChange = () => {
        dispatch({
            type: StoreActionType.SET_DARK_MODE,
            payload: {
                isDarkMode: !darkState,
            },
        });
        setDarkState(!darkState);
        LocalStorageWrapper.setItemToLocalStorage('isDarkMode', !darkState);
    };

    const handleMenuToggle = () => {
        dispatch({
            type: StoreActionType.SET_MENU_OPEN,
            payload: {
                isMenuOpen: !menuStore?.isMenuOpen,
            },
        });
        setPaperClassName(!menuStore?.isMenuOpen ? 'menuOpened' : 'menuClosed');
        setShowLogo(!menuStore?.isMenuOpen);
    };

    useEffect(() => {
        // Initialize drawer paper class
        const drawerPaper = document.querySelector('.MuiDrawer-paper');
        if (drawerPaper) {
            drawerPaper.classList.toggle('menu-closed', !menuStore?.isMenuOpen);
        }
    }, [menuStore?.isMenuOpen]);

    useEffect(() => {
        setPaperClassName(menuStore?.isMenuOpen ? 'menuOpened' : 'menuClosed');
        setShowLogo(menuStore?.isMenuOpen);
    }, [menuStore?.isMenuOpen]);

    const _onClickLogo = () => {
        if (isMobile) _menuClose();
        history.push(PageUrl.home.url);
        window.scrollTo(0, 0);
    };

    const _onClickSignOut = () => {
        //props.history.push(PageUrl.signOut.url);
        authService.logout();
    };
    const getAllActiveCountryLanguagesForBackoffice = () => {
        GeneralQuery.GetAllActiveCountryLanguagesForBackoffice(instanceId)
            .then((res: any) => {
                if (res) {
                    setCountryDatas(res.data);
                    localStorage.setItem('countryData', JSON.stringify(res.data) || '');
                    // Helper function to check if country selection is needed
                    const needsCountrySelection = () => {
                        const stored = localStorage.getItem('selectedCountry');
                        return stored === 'undefined' || !stored;
                    };

                    if (needsCountrySelection()) {
                        const defaultCountry = res.data?.boSupportedCountries?.find(
                            (country: any) => country.isDefault
                        );
                        localStorage.setItem('selectedCountry', JSON.stringify(defaultCountry));
                    }
                }
            })
            .catch((error: any) => {
                console.log('Hata :>> ', error);
            });
    };

    const loginCheck = () => {
        authService.getUserInfo().then((getUserInfo: any) => {
            if (!Helper.isNil(getUserInfo)) {
                let decodedToken: any = jwt_decode(getUserInfo.access_token);
                setUserName(decodedToken.preferred_username);
                const initials = decodedToken.preferred_username
                    .toLowerCase()
                    .split(' ')
                    .map((n: string) => n[0])
                    .join('')
                    .toUpperCase();
                setUserInitials(initials);
                setIsLogin(true);
                setTimeout(function () {
                    GetMenuList();
                }, 1000);
            } else {
                setTimeout(function () {
                    loginCheck();
                }, 100);
            }
        });
    };

    const GetMenuList = () => {
        MenuQuery.GetMenuList().then((res: any) => {
            if (res) {
                dispatch({
                    type: StoreActionType.SET_MENU_CONTENT,
                    info: res,
                });
                localStorage.setItem(
                    'menuList_' +
                        import.meta.env.VITE_ENVIRONMENT +
                        '_' +
                        JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.regionId?.toString(),
                    JSON.stringify(res)
                );
            }
        });
    };

    const handleClickIcon = () => {
        AuthQuery.Logout()
            .catch((error: any) => {
                console.error('Logout error:', error);
            })
            .finally(() => {
                _onClickSignOut();
            });
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value.toLowerCase();
        setSearchQuery(query);

        if (!query) {
            setFilteredMenuContent(null);
            return;
        }

        const menuList = menuContentStore?.menuContent?.menuItem || [];
        const filtered = filterHeaderMenuByQuery(menuList, query);
        setFilteredMenuContent({ menuItem: filtered });
    };

    const handleHamburgerClick = () => {
        handleMenuToggle();
    };

    return (
        <ThemeProvider theme={darkTheme}>
            <header className={'sc-header ' + (isLogin ? ' login' : ' no-login')}>
                <div className={paperClassName} style={{ position: 'relative' }}>
                    {/* Accordion Content */}
                    {isHeaderAccordionOpen && (
                        <div className="header-accordion-content open">
                            <Box
                                sx={{
                                    width: '100%',
                                    padding: '1rem',
                                    gap: '1rem',
                                    backgroundColor: mainColor,
                                    borderRadius: 2,
                                    marginBottom: '2rem',
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Grid
                                    container
                                    spacing={2}
                                    alignItems="center"
                                    sx={{
                                        gap: '1rem',
                                        justifyContent: 'space-between',
                                        marginTop: '0.2rem',
                                        marginLeft: '0.5rem',
                                        width: '100%',
                                    }}
                                >
                                    <div>
                                        <SCUIHeaderSearch />
                                    </div>
                                    <div style={{ display: 'flex', gap: '3rem', alignItems: 'center' }}>
                                        {envVisible && <SCUIHoverEnvironment />}
                                        <SCUIStoreSelect />
                                        <Tooltip
                                            title={t('general.countrySelection') || 'Country Selection'}
                                            placement="bottom"
                                        >
                                            <span>
                                                {' '}
                                                <SCUIGeneralCountrySelectV2
                                                    countryDatas={countryDatas.boSupportedCountries}
                                                />
                                            </span>
                                        </Tooltip>
                                        <SCUIGeneralLanguageSelect languageDatas={countryDatas.boSupportedLanguages} />
                                        <IconButton
                                            onClick={handleProfileClick}
                                            size="large"
                                            sx={{
                                                padding: 0,
                                                marginLeft: '1rem',
                                                zIndex: 10,
                                                position: 'relative',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                },
                                                '&:focus': {
                                                    outline: 'none',
                                                },
                                                '&:active': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                },
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    bgcolor: '#5189eb',
                                                    fontSize: '1rem',
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {userInitials || <Person />}
                                            </Avatar>
                                        </IconButton>
                                        {/* Profile Menu - Modern detaylı versiyon */}
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={open}
                                            onClose={handleProfileClose}
                                            onClick={handleProfileClose}
                                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                            slotProps={{
                                                paper: {
                                                    sx: {
                                                        mt: 1.5,
                                                        minWidth: 220,
                                                        borderRadius: '8px',
                                                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                                                        '& .MuiList-root': {
                                                            padding: '8px',
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            <MenuItem
                                                sx={{
                                                    borderRadius: '6px',
                                                    mb: 0.5,
                                                    py: 1.5,
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(81, 137, 235, 0.08)',
                                                    },
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={2}>
                                                    <Avatar
                                                        sx={{
                                                            width: 36,
                                                            height: 36,
                                                            bgcolor: '#5189eb',
                                                            fontSize: '0.9rem',
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        {userInitials}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 600,
                                                                fontSize: '0.95rem',
                                                                color: darkState ? '#fff' : '#2c3e50',
                                                            }}
                                                        >
                                                            {userName}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: '0.8rem',
                                                                color: darkState
                                                                    ? 'rgba(255, 255, 255, 0.7)'
                                                                    : '#7f8c8d',
                                                                mt: 0.2,
                                                            }}
                                                        >
                                                            {t('header.userProfile')}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </MenuItem>
                                            <MenuItem
                                                sx={{
                                                    borderRadius: '6px',
                                                    py: 1.5,
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(81, 137, 235, 0.08)',
                                                    },
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={2}>
                                                    <SCUIDarkModeSwitch
                                                        checked={darkState}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            handleThemeChange();
                                                        }}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.9rem',
                                                            fontWeight: 500,
                                                            color: darkState ? 'rgba(255, 255, 255, 0.9)' : '#34495e',
                                                        }}
                                                    >
                                                        {t('general.darkMode')}
                                                    </Typography>
                                                </Stack>
                                            </MenuItem>
                                            {/* Request Role - Only shown if user doesn't have storeCode */}
                                            {!Helper.getStoreCode() && (
                                                <>
                                                    <MenuItem
                                                        onClick={handleRoleRequest}
                                                        sx={{
                                                            borderRadius: '6px',
                                                            py: 1.5,
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(81, 137, 235, 0.08)',
                                                                '& .MuiSvgIcon-root, & .MuiTypography-root': {
                                                                    color: '#5189eb',
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="center" spacing={2}>
                                                            <SecurityIcon
                                                                sx={{
                                                                    fontSize: '1.3rem',
                                                                    color: darkState
                                                                        ? 'rgba(255, 255, 255, 0.7)'
                                                                        : '#7f8c8d',
                                                                }}
                                                            />
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '0.9rem',
                                                                    fontWeight: 500,
                                                                    color: darkState
                                                                        ? 'rgba(255, 255, 255, 0.9)'
                                                                        : '#34495e',
                                                                }}
                                                            >
                                                                {t('header.requestRole')}
                                                            </Typography>
                                                        </Stack>
                                                    </MenuItem>
                                                    <MenuItem
                                                        onClick={handleMyRoleRequests}
                                                        sx={{
                                                            borderRadius: '6px',
                                                            py: 1.5,
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(81, 137, 235, 0.08)',
                                                                '& .MuiSvgIcon-root, & .MuiTypography-root': {
                                                                    color: '#5189eb',
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="center" spacing={2}>
                                                            <AssignmentIndIcon
                                                                sx={{
                                                                    fontSize: '1.3rem',
                                                                    color: darkState
                                                                        ? 'rgba(255, 255, 255, 0.7)'
                                                                        : '#7f8c8d',
                                                                }}
                                                            />
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '0.9rem',
                                                                    fontWeight: 500,
                                                                    color: darkState
                                                                        ? 'rgba(255, 255, 255, 0.9)'
                                                                        : '#34495e',
                                                                }}
                                                            >
                                                                {t('header.myRoleRequests')}
                                                            </Typography>
                                                        </Stack>
                                                    </MenuItem>
                                                </>
                                            )}
                                            <MenuItem
                                                onClick={handleLogout}
                                                sx={{
                                                    borderRadius: '6px',
                                                    py: 1.5,
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(231, 76, 60, 0.08)',
                                                        '& .MuiSvgIcon-root, & .MuiTypography-root': {
                                                            color: '#e74c3c',
                                                        },
                                                    },
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={2}>
                                                    <LogoutIcon
                                                        sx={{
                                                            fontSize: '1.3rem',
                                                            color: darkState ? 'rgba(255, 255, 255, 0.7)' : '#7f8c8d',
                                                        }}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.9rem',
                                                            fontWeight: 500,
                                                            color: darkState ? 'rgba(255, 255, 255, 0.9)' : '#34495e',
                                                        }}
                                                    >
                                                        {t('header.logout')}
                                                    </Typography>
                                                </Stack>
                                            </MenuItem>
                                        </Menu>
                                    </div>
                                </Grid>
                            </Box>
                        </div>
                    )}
                    {/* Accordion Toggle Icon - Sağ tarafta */}
                    {/* <IconButton
                        onClick={() => setHeaderAccordionOpen((open) => !open)}
                        sx={{
                            position: 'absolute',
                            right: -60,
                            top: 16,
                            transform: 'none',
                            zIndex: 1,
                            border: '1.5px solid #1976d2',
                            background: '#fff',
                            color: '#1976d2',
                            boxShadow: 1,
                            pointerEvents: 'auto',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                            },
                            '&:focus': {
                                outline: 'none',
                            },
                        }}
                    >
                        {isHeaderAccordionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton> */}
                </div>

                <Drawer
                    variant="permanent"
                    open={menuStore?.isMenuOpen}
                    slotProps={{
                        paper: {
                            sx: {
                                width: getDrawerWidth(),
                                height: '100%',
                                position: 'fixed',
                                left: 0,
                                top: 0,
                                backgroundColor: mainColor,
                                borderRight: `1px solid rgba(${darkState ? '255, 255, 255' : '0, 0, 0'}, 0.12)`,
                                transition: 'all 0.3s ease',
                                '& .MuiDrawer-paper': {
                                    width: getDrawerPaperWidth(),
                                    overflowX: 'hidden',
                                    overflowY: 'hidden',
                                    transition: 'all 0.3s ease',
                                    backgroundColor: mainColor,
                                },
                            },
                        },
                    }}
                >
                    <div
                        className="drawer-area"
                        style={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                borderBottom: `1px solid rgba(${darkState ? '255, 255, 255' : '0, 0, 0'}, 0.12)`,
                                flexShrink: 0,
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '1rem',
                                }}
                            >
                                {showLogo && menuStore?.isMenuOpen && (
                                    <div
                                        className="drawerHeaderIcon"
                                        onClick={_onClickLogo}
                                        style={{
                                            flex: 1,
                                            marginRight: '1rem',
                                        }}
                                    >
                                        <img
                                            src={shouldShowNewYearLogo() ? newYearLogo : logo}
                                            alt=""
                                            style={{
                                                width: '100%',
                                                maxWidth: '130px',
                                                height: 'auto',
                                                display: 'block',
                                            }}
                                        />
                                    </div>
                                )}

                                <IconButton
                                    onClick={handleHamburgerClick}
                                    size="large"
                                    edge="end"
                                    color="inherit"
                                    aria-label="menu"
                                    sx={{
                                        color: '#5189eb',
                                        flexShrink: 0,
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '2rem',
                                            fontWeight: 'bold',
                                        },
                                    }}
                                >
                                    <MenuIcon />
                                </IconButton>
                            </div>

                            {menuStore?.isMenuOpen && (
                                <Typography
                                    sx={{
                                        padding: '0 1rem 1rem 1rem',
                                        color: '#5189eb',
                                        fontSize: '1rem',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                    }}
                                >
                                    {t('header.welcome')}
                                    {` ${userName}`}!
                                    <WavingHandIcon
                                        sx={{
                                            color: '#FFD700',
                                            verticalAlign: 'middle',
                                            marginLeft: '4px',
                                        }}
                                    />
                                    <Tooltip title={t('menu.refreshCacheTooltip')} placement="top">
                                        <IconButton
                                            onClick={handleRefreshMenu}
                                            disabled={isRefreshingMenu}
                                            size="small"
                                            sx={{
                                                marginLeft: 'auto',
                                                color: '#5189eb',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(81, 137, 235, 0.1)',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: '1.2rem',
                                                },
                                            }}
                                        >
                                            <RefreshIcon
                                                sx={{
                                                    animation: isRefreshingMenu ? 'spin 1s linear infinite' : 'none',
                                                    '@keyframes spin': {
                                                        '0%': {
                                                            transform: 'rotate(0deg)',
                                                        },
                                                        '100%': {
                                                            transform: 'rotate(360deg)',
                                                        },
                                                    },
                                                }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </Typography>
                            )}
                        </div>

                        <div
                            style={{
                                flex: 1,
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {menuStore?.isMenuOpen && (
                                <div style={{ padding: '1rem', width: '100%', flexShrink: 0, position: 'relative' }}>
                                    <input
                                        type="text"
                                        placeholder={t('general.menuSearch')}
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        style={{
                                            width: '100%',
                                            padding: '0.8rem',
                                            paddingRight: '2.5rem',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '4px',
                                            fontSize: '1.2rem',
                                            outline: 'none',
                                            transition: 'border-color 0.2s ease',
                                            backgroundColor: darkState ? '#2d2d2d' : '#fff',
                                            color: darkState ? '#fff' : '#000',
                                        }}
                                    />
                                    {searchQuery && (
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSearchQuery('');
                                                setFilteredMenuContent(null);
                                            }}
                                            sx={{
                                                position: 'absolute',
                                                right: '1.5rem',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: darkState ? '#fff' : '#666',
                                                padding: '4px',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                },
                                            }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </div>
                            )}
                            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                                <SCUIMainMenu
                                    id={'main-menu'}
                                    classNameFlatImg={darkState ? 'darkModeFlatImg' : 'lightModeFlatImg'}
                                    onClickNestedMenu={clickNestedMenu}
                                    menuContent={filteredMenuContent || menuContentStore?.menuContent}
                                    isCollapsed={!menuStore?.isMenuOpen}
                                    isOpen={menuStore?.isMenuOpen}
                                />
                            </div>
                        </div>
                    </div>
                </Drawer>
            </header>
        </ThemeProvider>
    );
};
